import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { cacheTrack } from "../lib/offline";
import { resolveMediaUrl } from "../lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [track, setTrack] = useState(null);
  const [queue, setQueueState] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("none"); // "none" | "one" | "all"

  const repeatRef = useRef(repeat);
  const queueRef = useRef(queue);
  const currentIndexRef = useRef(currentIndex);
  const shuffleRef = useRef(shuffle);

  useEffect(() => { repeatRef.current = repeat; }, [repeat]);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    audioRef.current = a;

    const onTime = () => { if (a.duration) setProgress(a.currentTime / a.duration); };
    const onDur = () => setDuration(a.duration || 0);
    const onEnd = () => {
      const r = repeatRef.current;
      const q = queueRef.current;
      const idx = currentIndexRef.current;
      if (r === "one") {
        a.currentTime = 0;
        a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } else if (q.length > 1) {
        let nextIdx;
        if (shuffleRef.current) {
          do { nextIdx = Math.floor(Math.random() * q.length); } while (nextIdx === idx && q.length > 1);
        } else {
          nextIdx = idx + 1;
        }
        if (nextIdx < q.length || r === "all") {
          const safeIdx = nextIdx % q.length;
          const nextTrack = q[safeIdx];
          setCurrentIndex(safeIdx);
          setTrack(nextTrack);
          setProgress(0);
          setDuration(0);
          a.src = resolveMediaUrl(nextTrack.media_url);
          a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
          if (nextTrack.type !== "video") cacheTrack(nextTrack).catch(() => {});
        } else {
          setIsPlaying(false);
        }
      } else {
        setIsPlaying(false);
      }
    };

    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const play = useCallback((t, queueTracks = null, startIdx = 0) => {
    if (!t || !audioRef.current) return;
    if (queueTracks) {
      setQueueState(queueTracks);
      setCurrentIndex(startIdx);
      queueRef.current = queueTracks;
      currentIndexRef.current = startIdx;
    }
    if (!audioRef.current.src || track?.id !== t.id) {
      audioRef.current.src = resolveMediaUrl(t.media_url);
    }
    setTrack(t);
    setProgress(0);
    setDuration(0);
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    if (t.type !== "video") cacheTrack(t).catch(() => {});
  }, [track]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (!track) return;
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  }, [track, isPlaying]);

  const seekTo = useCallback((ratio) => {
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = ratio * audioRef.current.duration;
      setProgress(ratio);
    }
  }, []);

  const playNext = useCallback(() => {
    const q = queueRef.current;
    const idx = currentIndexRef.current;
    if (!q.length) return;
    let nextIdx;
    if (shuffleRef.current) {
      do { nextIdx = Math.floor(Math.random() * q.length); } while (nextIdx === idx && q.length > 1);
    } else {
      nextIdx = (idx + 1) % q.length;
    }
    const nextTrack = q[nextIdx];
    if (!nextTrack || !audioRef.current) return;
    setCurrentIndex(nextIdx);
    setTrack(nextTrack);
    setProgress(0);
    setDuration(0);
    audioRef.current.src = resolveMediaUrl(nextTrack.media_url);
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    if (nextTrack.type !== "video") cacheTrack(nextTrack).catch(() => {});
  }, []);

  const playPrev = useCallback(() => {
    const q = queueRef.current;
    const idx = currentIndexRef.current;
    if (!audioRef.current) return;
    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      setProgress(0);
      return;
    }
    if (!q.length) return;
    const prevIdx = idx === 0 ? q.length - 1 : idx - 1;
    const prevTrack = q[prevIdx];
    if (!prevTrack) return;
    setCurrentIndex(prevIdx);
    setTrack(prevTrack);
    setProgress(0);
    setDuration(0);
    audioRef.current.src = prevTrack.media_url;
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    if (prevTrack.type !== "video") cacheTrack(prevTrack).catch(() => {});
  }, []);

  const setQueue = useCallback((tracks, startIndex = 0) => {
    setQueueState(tracks);
    setCurrentIndex(startIndex);
    queueRef.current = tracks;
    currentIndexRef.current = startIndex;
  }, []);

  const cycleRepeat = useCallback(() => {
    setRepeat((r) => r === "none" ? "all" : r === "all" ? "one" : "none");
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        track, isPlaying, progress, duration, volume, setVolume,
        play, pause, toggle, seekTo,
        queue, currentIndex, setQueue,
        playNext, playPrev,
        shuffle, setShuffle,
        repeat, cycleRepeat,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within PlayerProvider");
  return ctx;
};
